import type { Cue, Segment } from './types';

const CJK = /[㐀-鿿豈-﫿]/;

/** "01:02:03,456" | "02:03.456" | "3.5s" | "1500ms" → milliseconds */
export function parseTimestamp(value: string): number {
  const v = value.trim();
  const offset = /^([\d.]+)(h|m|s|ms)$/.exec(v);
  if (offset) {
    const n = Number(offset[1]);
    const unit = offset[2];
    const factor = unit === 'h' ? 3_600_000 : unit === 'm' ? 60_000 : unit === 's' ? 1000 : 1;
    return Math.round(n * factor);
  }
  const parts = v.replace(',', '.').split(':');
  if (parts.length < 2 || parts.length > 3 || parts.some((p) => p === '' || Number.isNaN(Number(p)))) {
    throw new Error(`Invalid timestamp: ${value}`);
  }
  const seconds = parts.reduce((total, part) => total * 60 + Number(part), 0);
  return Math.round(seconds * 1000);
}

function decodeEntities(text: string): string {
  return text
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

const clean = (text: string) => decodeEntities(text.replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ').trim();

/** Split cue lines into English and Chinese by script, so bilingual SRT files work. */
function splitLines(lines: string[]): { en: string; zh: string } {
  const en: string[] = [];
  const zh: string[] = [];
  for (const raw of lines) {
    const line = clean(raw);
    if (!line) continue;
    (CJK.test(line) ? zh : en).push(line);
  }
  return { en: en.join(' '), zh: zh.join(' ') };
}

/** SRT and WebVTT share the "start --> end" block structure. */
export function parseSrtOrVtt(text: string): Cue[] {
  const blocks = text.replace(/\r/g, '').replace(/^﻿/, '').split(/\n{2,}/);
  const cues: Cue[] = [];
  for (const block of blocks) {
    const lines = block.split('\n');
    const timeIdx = lines.findIndex((l) => l.includes('-->'));
    if (timeIdx < 0) continue;
    const [startRaw, endRaw] = lines[timeIdx]!.split('-->');
    if (!startRaw || !endRaw) continue;
    const startMs = parseTimestamp(startRaw);
    const endMs = parseTimestamp(endRaw.trim().split(/\s+/)[0]!);
    const { en, zh } = splitLines(lines.slice(timeIdx + 1));
    if (en || zh) cues.push({ startMs, endMs, en, zh });
  }
  return cues;
}

/** TTML (BBC captions). Regex based so it runs in Workers, which have no DOMParser. */
export function parseTtml(text: string): Cue[] {
  const cues: Cue[] = [];
  const paragraph = /<(?:\w+:)?p\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?p>/g;
  for (const match of text.matchAll(paragraph)) {
    const attrs = match[1] ?? '';
    const begin = /\bbegin="([^"]+)"/.exec(attrs)?.[1];
    const end = /\bend="([^"]+)"/.exec(attrs)?.[1];
    if (!begin || !end) continue;
    const body = (match[2] ?? '').replace(/<br\s*\/?>/gi, '\n');
    const { en, zh } = splitLines(body.split('\n'));
    if (en || zh) cues.push({ startMs: parseTimestamp(begin), endMs: parseTimestamp(end), en, zh });
  }
  return cues;
}

/** JSON: [{start,end,en,zh}] in seconds, or [{startMs,endMs,en,zh}]. */
export function parseJsonCues(text: string): Cue[] {
  const data: unknown = JSON.parse(text);
  const list = Array.isArray(data) ? data : (data as { segments?: unknown }).segments;
  if (!Array.isArray(list)) throw new Error('JSON subtitles must be an array or {segments: [...]}');
  return list.map((item: Record<string, unknown>) => {
    const startMs = item.startMs !== undefined ? Number(item.startMs) : Math.round(Number(item.start) * 1000);
    const endMs = item.endMs !== undefined ? Number(item.endMs) : Math.round(Number(item.end) * 1000);
    return { startMs, endMs, en: String(item.en ?? '').trim(), zh: String(item.zh ?? '').trim() };
  });
}

export function parseSubtitles(text: string, filename = ''): Cue[] {
  const name = filename.toLowerCase();
  const head = text.trimStart().slice(0, 200);
  let cues: Cue[];
  if (name.endsWith('.json') || head.startsWith('[') || head.startsWith('{')) cues = parseJsonCues(text);
  else if (name.endsWith('.ttml') || name.endsWith('.xml') || /<tt[\s>]/.test(text)) cues = parseTtml(text);
  else cues = parseSrtOrVtt(text);
  return validateCues(cues);
}

export function validateCues(cues: Cue[]): Cue[] {
  if (cues.length === 0) throw new Error('没有找到字幕内容');
  if (cues.length > 20_000) throw new Error('字幕条数过多');
  const sorted = [...cues].sort((a, b) => a.startMs - b.startMs);
  for (const cue of sorted) {
    if (!Number.isFinite(cue.startMs) || !Number.isFinite(cue.endMs) || cue.startMs < 0 || cue.endMs <= cue.startMs) {
      throw new Error(`字幕时间不正确：${cue.en || cue.zh}`);
    }
  }
  return sorted;
}

const SENTENCE_END = /[.!?…]["'”’)]*$/;

/**
 * Broadcast captions are cut by screen width, not by sentence. Join consecutive
 * cues until a sentence ends, so 精听 can loop one whole sentence.
 */
export function mergeIntoSentences(cues: Cue[], maxMs = 15_000): Cue[] {
  const out: Cue[] = [];
  let current: Cue | null = null;
  for (const cue of cues) {
    if (current && cue.startMs - current.endMs < 1500 && cue.endMs - current.startMs <= maxMs) {
      current = {
        startMs: current.startMs,
        endMs: cue.endMs,
        en: `${current.en} ${cue.en}`.trim(),
        zh: `${current.zh}${cue.zh}`.trim(),
      };
    } else {
      if (current) out.push(current);
      current = { ...cue };
    }
    if (SENTENCE_END.test(current.en)) {
      out.push(current);
      current = null;
    }
  }
  if (current) out.push(current);
  return out;
}

/** Index of the segment playing at timeMs, or -1. Segments must be sorted. */
export function findSegmentIndex(segments: Pick<Segment, 'startMs' | 'endMs'>[], timeMs: number): number {
  let lo = 0;
  let hi = segments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = segments[mid]!;
    if (timeMs < s.startMs) hi = mid - 1;
    else if (timeMs >= s.endMs) lo = mid + 1;
    else return mid;
  }
  return -1;
}

export interface Token {
  text: string;
  /** Lowercase lookup form; absent for spaces and punctuation. */
  word?: string;
}

export function tokenize(sentence: string): Token[] {
  const tokens: Token[] = [];
  for (const match of sentence.matchAll(/([A-Za-z]+(?:['’][A-Za-z]+)*)|([^A-Za-z]+)/g)) {
    if (match[1]) tokens.push({ text: match[1], word: match[1].toLowerCase().replace(/’/g, "'") });
    else tokens.push({ text: match[2]! });
  }
  return tokens;
}

export function formatSrtTime(ms: number): string {
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  const r = ms % 1000;
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(r, 3)}`;
}

export function toSrt(cues: Cue[]): string {
  return cues
    .map((c, i) => `${i + 1}\n${formatSrtTime(c.startMs)} --> ${formatSrtTime(c.endMs)}\n${[c.en, c.zh].filter(Boolean).join('\n')}`)
    .join('\n\n');
}
